import { useMemo } from 'react';
import { useAppSelector } from './useAppSelector';
import { User, Workspace, WorkspaceMemberRole } from '../types/common';

/**
 * Mirrors the backend's Permission enum / ROLE_PERMISSIONS matrix
 * (agile-edge-backend/src/common/permissions/permission.enum.ts). Keep the
 * two in sync by hand - there's no shared package between the repos.
 */
export enum Permission {
  WORKSPACE_MANAGE = 'workspace:manage',
  MEMBER_MANAGE = 'member:manage',
  BOARD_CREATE = 'board:create',
  BOARD_MANAGE = 'board:manage',
  BOARD_VIEW = 'board:view',
  FOLDER_MANAGE = 'folder:manage',
  FOLDER_VIEW = 'folder:view',
  TASK_CREATE = 'task:create',
  TASK_VIEW = 'task:view',
  TASK_EDIT = 'task:edit',
  TASK_DELETE = 'task:delete',
}

const ALL_PERMISSIONS = Object.values(Permission);

const ROLE_PERMISSIONS: Record<WorkspaceMemberRole, Permission[]> = {
  [WorkspaceMemberRole.OWNER]: ALL_PERMISSIONS,
  [WorkspaceMemberRole.ADMIN]: ALL_PERMISSIONS,
  [WorkspaceMemberRole.MEMBER]: [
    Permission.BOARD_CREATE,
    Permission.BOARD_VIEW,
    Permission.FOLDER_VIEW,
    Permission.FOLDER_MANAGE,
    Permission.TASK_CREATE,
    Permission.TASK_VIEW,
    Permission.TASK_EDIT,
  ],
  [WorkspaceMemberRole.VIEWER]: [
    Permission.BOARD_VIEW,
    Permission.FOLDER_VIEW,
    Permission.TASK_VIEW,
  ],
};

/**
 * Pure, hook-free role resolution - use this (not the hook) when computing
 * roles for several workspaces in one render, e.g. inside a `.map()` over a
 * workspace list, where calling a hook per-iteration would break the rules
 * of hooks.
 */
export function getWorkspaceRole(
  workspace: Workspace | null | undefined,
  user: User | null | undefined,
): WorkspaceMemberRole | null {
  if (!workspace || !user) return null;
  if (String(workspace.ownerId) === String(user.id)) return WorkspaceMemberRole.OWNER;
  const member = (workspace.members || [])
    .filter(Boolean)
    .find((m) => String(m?.userId) === String(user.id));
  return member?.role ?? null;
}

export function roleHasPermission(
  role: WorkspaceMemberRole | null,
  permission: Permission,
): boolean {
  return !!role && (ROLE_PERMISSIONS[role] || []).includes(permission);
}

/**
 * Resolves the caller's role/permissions for a workspace. Defaults to
 * `state.workspaces.currentWorkspace` when no `workspaceId` is given (or it
 * doesn't match anything loaded) - the common case for pages already
 * scoped to "the" workspace in view.
 *
 * This is a UI convenience layer only, not the source of truth - the
 * backend's PermissionsGuard enforces the real access control. A stale or
 * not-yet-loaded workspace here just means a button is hidden/shown a beat
 * late, not a security gap.
 */
export function usePermissions(workspaceId?: string | null) {
  const user = useAppSelector((state) => state.auth.user);
  const { currentWorkspace, workspaces } = useAppSelector((state) => state.workspaces);

  const workspace = useMemo(() => {
    if (workspaceId) {
      if (currentWorkspace?.id === workspaceId) return currentWorkspace;
      return workspaces.find((w) => w.id === workspaceId) || null;
    }
    return currentWorkspace;
  }, [workspaceId, currentWorkspace, workspaces]);

  const role = useMemo<WorkspaceMemberRole | null>(
    () => getWorkspaceRole(workspace, user),
    [workspace, user],
  );

  const can = (permission: Permission): boolean => roleHasPermission(role, permission);

  const isOwner = role === WorkspaceMemberRole.OWNER;
  const isOwnerOrAdmin = role === WorkspaceMemberRole.OWNER || role === WorkspaceMemberRole.ADMIN;

  return {
    role,
    isOwner,
    isOwnerOrAdmin,
    can,
    canManageWorkspace: can(Permission.WORKSPACE_MANAGE),
    canManageMembers: can(Permission.MEMBER_MANAGE),
    canCreateBoard: can(Permission.BOARD_CREATE),
    canManageBoard: can(Permission.BOARD_MANAGE),
    canManageFolder: can(Permission.FOLDER_MANAGE),
    canCreateTask: can(Permission.TASK_CREATE),
    canEditTask: can(Permission.TASK_EDIT),
    canDeleteTask: can(Permission.TASK_DELETE),
  };
}
